import Map, { Layer, NavigationControl, Source, ViewStateChangeEvent, Popup, MapRef, MapMouseEvent } from 'react-map-gl';
import './mapBox.css';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignalPopupContent from '../../components/popups/SignalClickPopupContent';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../core/store';
import SignalHoverPopupContent from '../../components/popups/SignalHoverPopupContent';
import { EPointType, TFeature, TPointSummary, TVesselInfo, TVoyageSummary } from '../../mapTypes';
import {
	emptyVesselLastStoppageEventsAction,
	getVesselLastStoppageEventsRequestAction,
} from '../../slices/vesselLastStoppageEventsSlice';
import { getVesselsLastSignalRequestAction } from '../../slices/vesselsLastSignalSlice';
import { emptyVesselLastVoyagesAction, getVesselLastVoyagesRequestAction } from '../../slices/vesselLastVoyagesSlice';
import {
	lineLayerStyle,
	lineLayerStyleForDailyPositions,
	pointArrowLayerStyle,
	pointArrowLayerStyleForDailyPostitons,
	pointArrowLayerStyleForOngoingVoyage,
	pointArrowLayerStyleForVoyage,
	polygonLayerStyle,
	portCallsLayerStyle,
	stoppagesLayerStyle,
} from '../../constants/mapLayers';

import { emptyVesselLastPortCallsAction, getVesselLastPortCallsRequestAction } from '../../slices/vesselLastPortCallsSlice';
import { setSelectedVesselsForOutputRequest } from '../../../search/searchSlice';
import { selectSelectedSubscriptionFormData, selectSubscribedVesselsFormData } from '../../../../shared/sharedSelectors';
import { EOutputType, ESelectionType } from '../../../../shared/sharedEnums';
import { selectIsMsi, selectIsSubscriptionDataFetched } from '../../../../user-management/appSelectors';
import { selectSelectionData } from '../../../search/searchSelectors';
import VesselPopupSummary from '../../components/popups/VesselSummaryPopup';
import {
	createArrowIcon,
	findCenterPoint,
	fixAntimeridian,
	MapBoxGlobeMapStyles,
	MapBoxToken,
} from '../../../../utils/mapHelpers';
import MapStyleChanger from '../../components/mapOperations/MapStyleChanger';
import {
	getVesselOngoingVoyageSignalsRequestAction,
	selectVesselOngoingVoyageSignals,
} from '../../slices/vesselOngoingVoyageSignalsSlice';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getVesselVoyageDailySignalsRequestAction } from '../../slices/vesselVoyageDailySignalsSlice';
import VoyageHoverPopupContent from '../../components/popups/VoyageHoverPopupContent';
import { emptyVesselDailyPositionsAction, getVesselDailyPositionsRequestAction } from '../../slices/vesselDailyPositionsSlice';
import { calculateAnalysisDateRange } from '../../../date-range/utils/dateRangeHelpers';
import {
	selectVesselDailyPositionsAsLineString,
	selectVesselDailyPositionsWithoutLastSignalByImoNumber,
	selectVesselLastVoyagesData,
	selectVesselLastVoyagesLoading,
	selectVesselsLastSignalData,
	selectVesselsLastSignalLoading,
} from '../../mapSelectors';
import { getVesselOngoingVoyageDailySignalsRequestAction } from '../../slices/vesselOngoingVoyageDailySignalsSlice';
import {
	getDefaultActivityDateRangeRequestAction,
	selectDefaultActivityDateRange,
	selectDefaultActivityDateRangeLoading,
} from '../../slices/defaultActivityDateRangeSlice';
import MapLegendInfo from '../../../../shared/containers/drawers/MapLegendInfo';
import MapLayerChanger from '../../components/mapOperations/MapLayerChanger';
import { DEFAULT_ZOOM_LEVEL } from '../../constants/mapConstants';
import { addSeascapeLogActivity } from '../../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../../activity-logging/activityLogTypes';
import { trackEventWithContext, trackExceptionWithContext } from '../../../../services/application-insights/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsErrorTypes,
} from '../../../../services/application-insights/appInsightsConstants';
import { styleKeyToUrl, urlToStyleKey } from '../../../../utils/mapHelpers';
import PolygonLayerToggle from '../../components/mapOperations/PolygonLayerToggle';
import { detailedPolygonsActions, selectDetailedPolygons } from '../../slices/detailedPolygonsSlice';
import { showWarningAlert } from '../../../../services/globalAlertService';
import {
	initializeDateRangeFromMainMap,
	selectFeatureDateRangeAnalysisDateRange,
	selectFeatureDateRangeVesselLocationsDate,
} from '../../../date-range/slices/dateRangeSlice';
import { parseDate, CalendarDate } from '@internationalized/date';
import { DATE_RANGE_OPTIONS } from '../../../date-range/types/dateRangeTypes';
import { extractDateFromISO } from '../../../../utils/helpers';
import { getVesselCurrentStatusRequestAction } from '../../slices/vesselCurrentStatusSlice';
import DateRangeWithSlider from '../../../date-range/DateRangeWithSlider';
import { selectSelectedPortGeometry } from '../../../port/portSelector';
import * as turf from '@turf/turf';
import OpenSeaMapToggle from '../../components/mapOperations/OpenSeaMapToggle';

const defaultViewState = {
	latitude: 20,
	longitude: 20,
	zoom: DEFAULT_ZOOM_LEVEL,
};

const MapBox: FC = () => {
	const dispatch = useAppDispatch<AppDispatch>();
	const isMsi = useAppSelector(selectIsMsi);

	// const { vesselsLastSignalData, vesselsLastSignalLoading } = useAppSelector((state: RootState) => state.vesselsLastSignalSlice);
	// Get only the vessel data (more stable reference)
	const vesselsLastSignalData = useAppSelector(selectVesselsLastSignalData);

	// Get only the loading state
	const vesselsLastSignalLoading = useAppSelector(selectVesselsLastSignalLoading);

	const vesselLastVoyagesData = useAppSelector(selectVesselLastVoyagesData);
	const vesselLastVoyagesLoading = useAppSelector(selectVesselLastVoyagesLoading);

	const { vesselLastStoppageEventsData, vesselLastStoppageEventsLoading } = useAppSelector(
		(state: RootState) => state.vesselLastStoppageEventsSlice,
	);
	const { vesselLastPortCallsData, vesselLastPortCallsLoading } = useAppSelector(
		(state: RootState) => state.vesselLastPortCallsSlice,
	);

	const { vesselVoyageDailySignalsData } = useAppSelector((state: RootState) => state.vesselVoyageDailySignalsSlice);

	const { vesselOngoingVoyageDailySignalsData } = useAppSelector(
		(state: RootState) => state.vesselOngoingVoyageDailySignalsSlice,
	);

	const selectionData = useAppSelector(selectSelectionData);
	const subscribedMsiVessels = useAppSelector(selectSubscribedVesselsFormData);
	const selectedSubscription = useAppSelector(selectSelectedSubscriptionFormData);
	const isSubscriptionReady = useAppSelector(selectIsSubscriptionDataFetched);
	const vesselOngoingVoyageSignals = useAppSelector(selectVesselOngoingVoyageSignals);
	const vesselDailyPositionsWithoutLastSignal = useAppSelector((state: RootState) =>
		selectionData && selectionData.outputType === EOutputType.Vessel && selectionData.selectedVesselsList.length > 0
			? selectVesselDailyPositionsWithoutLastSignalByImoNumber(state, selectionData.selectedVesselsList[0].imoNumber!)
			: null,
	);

	const vesselDailyPositionsAsLineString = useAppSelector(selectVesselDailyPositionsAsLineString);
	const detailedPolygons = useAppSelector(selectDetailedPolygons);
	const mainMapDateRange = useAppSelector(selectDefaultActivityDateRange);
	const mainMapDateRangeLoading = useAppSelector(selectDefaultActivityDateRangeLoading);

	// Read from the feature date range slice
	const vesselLocationsDate = useAppSelector(selectFeatureDateRangeVesselLocationsDate);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	// Read selected port geometry from port slice
	const selectedPortGeometry = useAppSelector(selectSelectedPortGeometry);

	const current_date = new Date();

	const [vesselLocationsSpecificDate, setVesselLocationsSpecificDate] = useState(
		vesselLocationsDate.specificDate ||
			new CalendarDate(current_date.getFullYear(), current_date.getMonth() + 1, current_date.getDate()).add({ days: 1 }),
	);

	const mapRef = useRef<MapRef>(null);
	const [viewState, setViewState] = useState(defaultViewState);

	const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);
	const [pointData, setPointData] = useState<TVesselInfo>();
	const [vesselsLastSignal, setVesselsLastSignal] = useState<TVesselInfo | null>(vesselsLastSignalData);
	const [vesselVoyages, setVesselVoyages] = useState<TVesselInfo | null>(null);
	const [vesselStoppageEvents, setVesselStoppageEvents] = useState<TVesselInfo | null>(null);
	const [vesselPortCalls, setVesselPortCalls] = useState<TVesselInfo | null>(null);
	const [vesselVoyageDailySignals, setVesselVoyageDailySignals] = useState<TVesselInfo | null>(null);
	const [vesselDailyPositions, setVesselDailyPositions] = useState<TVesselInfo | null>(null);
	const [vesselOngoingVoyageDailySignals, setVesselOngoingVoyageDailySignals] = useState<TVesselInfo | null>(null);
	const [vesselDailyPositionsAsLine, setVesselDailyPositionsAsLine] = useState<TVesselInfo | null>(null);
	const [voyageSummary, setVoyageSummary] = useState<TVoyageSummary>(null);
	const [pointSummary, setPointSummary] = useState<TPointSummary>(null);
	const [cursorStyle, setCursorStyle] = useState<string>('');
	const [settings] = useState({
		scrollZoom: true,
		boxZoom: true,
		dragRotate: false,
		dragPan: true,
		keyboard: true,
		doubleClickZoom: true,
		touchZoomRotate: false,
		touchPitch: true,
		minZoom: 1.75,
		maxZoom: 20,
		minPitch: 0,
		maxPitch: 85,
		antialias: false,
	});

	const [focusedVoyageId, setFocusedVoyageId] = useState<number>(0); // State to track point name
	const [mapLoaded, setMapLoaded] = useState(false);
	const [styleLoaded, setStyleLoaded] = useState(false);
	const [layerImagesAdded, setLayerImagesAdded] = useState(false);

	const [detailedPolygonsVisible, setDetailedPolygonsVisible] = useState(false);

	const [openSeaMapVisible, setOpenSeaMapVisible] = useState(false);

	const { selectedVesselsList, outputType, selectionType } = selectionData;

	const CLUSTER_THRESHOLD = 20000; // Threshold for number of signals to start clustering
	const MAX_DISPLAY_SIGNALS = 20000; // Max number of signals to display at once when clustering

	// Sample only for Summary output with more than CLUSTER_THRESHOLD signals
	const shouldCluster = outputType === EOutputType.Summary && (vesselsLastSignal?.features?.length ?? 0) > CLUSTER_THRESHOLD;

	// Pre-sort features once by a stable hash of imoNumber so the visible subset is always
	// the same "first N" features. Zooming in only ever appends — signals never disappear.
	const stableSortedFeatures = useMemo(() => {
		if (!shouldCluster || !vesselsLastSignal?.features) return vesselsLastSignal?.features ?? [];
		return [...vesselsLastSignal.features].sort((a, b) => {
			const hashA = ((a.properties?.imoNumber ?? 0) * 2654435761) >>> 0;
			const hashB = ((b.properties?.imoNumber ?? 0) * 2654435761) >>> 0;
			return hashA - hashB;
		});
	}, [vesselsLastSignal, shouldCluster]);

	// Linearly interpolate visible count from MAX_DISPLAY_SIGNALS at default zoom
	// up to the full fetched count at zoom 10+.
	const displaySignals = useMemo(() => {
		if (!vesselsLastSignal?.features) return null;

		// When a specific vessel or fleet is selected, filter immediately to prevent
		// stale signals from remaining visible during the render cycle before the
		// effect updates vesselsLastSignal (they would only clear on zoom change otherwise)
		if ((outputType === EOutputType.Vessel || outputType === EOutputType.Fleet) && selectedVesselsList?.length > 0) {
			const imoSet = new Set(selectedVesselsList.map((v) => v.imoNumber));
			const filtered = vesselsLastSignal.features.filter((f) => imoSet.has(f.properties!.imoNumber));
			return filtered.length > 0 ? { ...vesselsLastSignal, features: filtered } : null;
		}

		const total = stableSortedFeatures.length;
		if (!shouldCluster || total <= MAX_DISPLAY_SIGNALS) {
			return vesselsLastSignal;
		}
		const t = Math.min(1, Math.max(0, (zoomLevel * 1.5 - DEFAULT_ZOOM_LEVEL) / (8 - DEFAULT_ZOOM_LEVEL)));
		const maxForZoom = Math.round(MAX_DISPLAY_SIGNALS + t * (total - MAX_DISPLAY_SIGNALS));
		if (maxForZoom >= total) return vesselsLastSignal;
		return { ...vesselsLastSignal, features: stableSortedFeatures.slice(0, maxForZoom) };
	}, [vesselsLastSignal, stableSortedFeatures, shouldCluster, zoomLevel, outputType, selectedVesselsList]);

	// Group display features by sector color so each sector clusters independently
	const vesselsByColor = useMemo(() => {
		if (!displaySignals?.features) return {} as Record<string, { type: string; features: TFeature[] }>;
		return displaySignals.features.reduce(
			(acc, feature) => {
				const colorKey = (feature.properties?.color ?? 'default').replace('#', '');
				if (!acc[colorKey]) acc[colorKey] = { type: 'FeatureCollection', features: [] };
				acc[colorKey].features.push(feature);
				return acc;
			},
			{} as Record<string, { type: string; features: TFeature[] }>,
		);
	}, [displaySignals]);

	// Stable layer ID used as beforeId for layers that should render below vessel arrows
	const firstClusterLayerId = useMemo(() => {
		const firstKey = Object.keys(vesselsByColor)[0];
		return firstKey ? `vessel-arrows-${firstKey}` : undefined;
	}, [vesselsByColor]);

	// Get persisted style key, fallback to 'Msi_Dusk'
	const persistedStyleKey = localStorage.getItem('userMapStyle');
	const initialStyleKey = persistedStyleKey || 'Msi_Dusk';
	const [mapStyleKey, setMapStyleKey] = useState<string>(initialStyleKey);
	const mapStyleFromKey = styleKeyToUrl(mapStyleKey);
	const [mapStyle, setMapStyle] = useState(mapStyleFromKey);

	/**
	 * Effect to clean up vessel activities in the Redux store.
	 */
	const cleanUpVesselActivitiesInStore = useCallback(async () => {
		dispatch(emptyVesselLastVoyagesAction());
		dispatch(emptyVesselLastStoppageEventsAction());
		dispatch(emptyVesselLastPortCallsAction());
		dispatch(emptyVesselDailyPositionsAction());
	}, [dispatch]);

	/**
	 * Cleanup function to reset local state and clear vessel activities in the store.
	 */
	const cleanUp = useCallback(async () => {
		setPointData(null);
		setVesselsLastSignal(null);
		setVesselVoyages(null);
		setVesselStoppageEvents(null);
		setVesselPortCalls(null);
		setVesselVoyageDailySignals(null);
		setVesselDailyPositions(null);
		setVesselOngoingVoyageDailySignals(null);
		setVesselDailyPositionsAsLine(null);
		cleanUpVesselActivitiesInStore();
	}, [cleanUpVesselActivitiesInStore]);

	useEffect(() => {
		if (mainMapDateRange?.startDate && mainMapDateRange?.endDate && !analysisDateRange.selectedOption) {
			const currentDate = new Date();
			const specificDate = new CalendarDate(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()).add({
				days: 1,
			});

			const newDateRangeForAnalysis = calculateAnalysisDateRange(analysisDateRange, {
				start: parseDate(extractDateFromISO(mainMapDateRange.startDate)),
				end: specificDate,
			});

			dispatch(
				initializeDateRangeFromMainMap({
					selectedOption: DATE_RANGE_OPTIONS.SPECIFIC_RANGE,
					specificYear: analysisDateRange.specificYear || null,
					dateRange: analysisDateRange.dateRange || newDateRangeForAnalysis,
					specificDate,
				}),
			);
		}
	}, [mainMapDateRange, analysisDateRange, dispatch]);

	/**
	 * Fetch the last signal of vessels based on the current analysis date range and vessel location date.
	 * This function checks various conditions to avoid unnecessary requests and updates the state accordingly.
	 * It also ensures that the vessel location date is set to a specific date before making the request.
	 * If the conditions are not met, the function exits early to prevent redundant operations.
	 */
	const getVesselsLastSignal = useCallback(async () => {
		if (
			!subscribedMsiVessels ||
			subscribedMsiVessels.length === 0 ||
			!analysisDateRange ||
			!analysisDateRange.yearMonthRange ||
			!analysisDateRange.yearMonthRange.end ||
			vesselsLastSignalLoading ||
			!vesselLocationsDate.specificDate ||
			((vesselLocationsSpecificDate === vesselLocationsDate.specificDate ||
				(vesselLocationsSpecificDate &&
					vesselLocationsDate.specificDate &&
					vesselLocationsSpecificDate.compare(vesselLocationsDate.specificDate) === 0)) &&
				vesselsLastSignalData)
		) {
			return;
		}
		let date = vesselLocationsDate.specificDate ? vesselLocationsDate.specificDate.add({ days: 1 }).toDate('UTC') : new Date();

		dispatch(
			getVesselsLastSignalRequestAction({
				imoList: subscribedMsiVessels.map((v) => v.imoNumber),
				date: date.toISOString(),
				yearMonth: analysisDateRange.yearMonthRange.end,
			}),
		);

		setVesselLocationsSpecificDate(vesselLocationsDate.specificDate);
	}, [
		dispatch,
		subscribedMsiVessels,
		vesselLocationsDate.specificDate,
		vesselLocationsSpecificDate,
		vesselsLastSignalData,
		analysisDateRange,
		vesselsLastSignalLoading,
	]);

	/**
	 * Fetch the default activity date range and update the Redux store.
	 */
	const getDateRange = useCallback(async () => {
		if (mainMapDateRangeLoading || (mainMapDateRange && mainMapDateRange.startDate && mainMapDateRange.endDate)) return;
		dispatch(getDefaultActivityDateRangeRequestAction());
	}, [dispatch, mainMapDateRange, mainMapDateRangeLoading]);

	/**
	 * Function to smoothly fly the map to a specified longitude, latitude, and zoom level.
	 */
	const flyToPosition = useCallback(
		async (longitude: number, latitude: number, newZoomLevel: number) => {
			mapRef.current &&
				mapRef.current!.flyTo({
					center: [longitude, latitude],
					zoom: newZoomLevel,
					speed: 0.75,
				});
		},
		[mapRef],
	);

	/**
	 * Fetch last signal of vessels when the component mounts --- IGNORE ---
	 * This effect runs when the selected subscription or its readiness state changes.
	 * It ensures that the necessary data is fetched only when a valid subscription is selected and ready.
	 * The effect also calls cleanup functions to reset the state before fetching new data.
	 */
	useEffect(() => {
		if (selectedSubscription && isSubscriptionReady) {
			// cleanUp();
			getDateRange();
			getVesselsLastSignal();
			//flyToPosition(defaultViewState.longitude, defaultViewState.latitude, defaultViewState.zoom);
		}
	}, [getVesselsLastSignal, cleanUp, selectedSubscription, isSubscriptionReady, getDateRange]);

	const getLastActivities = useCallback(
		(imoNumber: number) => {
			if (imoNumber && analysisDateRange && analysisDateRange.dateRange) {
				const filteredVessel = subscribedMsiVessels!.find((v) => v.imoNumber === imoNumber) ?? null;

				const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
				const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();

				if (filteredVessel) {
					// Get last stoppage events
					dispatch(getVesselLastStoppageEventsRequestAction({ imoNumber, startDate, endDate }));
					// Get last port calls
					dispatch(getVesselLastPortCallsRequestAction({ imoNumber, startDate, endDate }));
					// Get last voyages and voyages daily signals
					dispatch(getVesselLastVoyagesRequestAction({ imoNumbers: [imoNumber], startDate, endDate }));
					dispatch(getVesselVoyageDailySignalsRequestAction({ imoNumbers: [imoNumber], startDate, endDate }));
					// Get daily positions
					dispatch(getVesselDailyPositionsRequestAction({ imoNumbers: [imoNumber], date: endDate }));
					// Get ongoing voyages and ongoing voyages daily signals
					dispatch(getVesselOngoingVoyageSignalsRequestAction({ imoNumber, date: endDate }));
					dispatch(getVesselOngoingVoyageDailySignalsRequestAction({ imoNumbers: [imoNumber], date: endDate }));
					// Get current status from daily signals
					dispatch(getVesselCurrentStatusRequestAction(imoNumber));
				} else {
					showWarningAlert('Vessel not found');
				}
			}
		},
		[subscribedMsiVessels, dispatch, analysisDateRange],
	);

	/**
	 * Handle vessel detail view when a vessel is selected.
	 * This function updates the selected vessel in the Redux store and fetches its last activities.
	 * It also logs the vessel click activity for analytics purposes.
	 */
	const handleVesselDetail = useCallback(
		(imoNumber: number) => {
			try {
				if (imoNumber) {
					// Track vessel detail view attempt
					trackEventWithContext(AppInsightsActions.MAP_VESSEL_DETAIL_VIEW, {
						component: AppInsightsComponents.MAP_BOX_COMPONENT,
						action: AppInsightsActions.MAP_VESSEL_DETAIL_VIEW,
						additionalData: {
							imoNumber,
							requestSource: 'handleVesselDetail',
						},
					});

					const filteredVessel = subscribedMsiVessels!.find((v) => v.imoNumber === imoNumber) ?? null;

					if (filteredVessel) {
						dispatch(
							setSelectedVesselsForOutputRequest({
								outputSelection: {
									selectedVesselsList: [filteredVessel],
									outputType: EOutputType.Vessel,
									selectionType: ESelectionType.Vessel,
									selectionName: `${filteredVessel.imoNumber} - ${filteredVessel.name}`,
									selectionTypeObject: filteredVessel,
								},
							}),
						);

						getLastActivities(imoNumber);

						// Track successful vessel selection
						trackEventWithContext(AppInsightsActions.MAP_VESSEL_SELECT, {
							component: AppInsightsComponents.MAP_BOX_COMPONENT,
							action: AppInsightsActions.MAP_VESSEL_SELECT,
							additionalData: {
								vesselName: filteredVessel.name,
								imoNumber: filteredVessel.imoNumber,
								sector: filteredVessel.sectorName,
								companyName: filteredVessel.companyName,
							},
						});

						const payload = JSON.stringify({ ImoNumber: imoNumber, Name: filteredVessel.name });
						const eventId = SeascapeActivityLogConstants.MAP_VESSEL_CLICKED;
						addSeascapeLogActivity({ eventId, payload });
					} else {
						// Track vessel not found error
						trackExceptionWithContext(new Error('Vessel not found in subscription'), {
							component: AppInsightsComponents.MAP_BOX_COMPONENT,
							action: AppInsightsActions.MAP_VESSEL_DETAIL_VIEW,
							additionalData: {
								errorType: AppInsightsErrorTypes.MAP_VESSEL_DATA_ERROR,
								imoNumber,
								vesselCount: subscribedMsiVessels?.length || 0,
							},
						});
						showWarningAlert('Vessel not found');
					}
				}
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_VESSEL_DETAIL_VIEW,
					additionalData: {
						errorType: AppInsightsErrorTypes.MAP_VESSEL_DATA_ERROR,
						imoNumber,
					},
				});
			}
		},
		[subscribedMsiVessels, dispatch, getLastActivities, addSeascapeLogActivity],
	);

	/**
	 * Convert pixel offset to latitudinal degrees.
	 * @param offsetPixels - Number of pixels to offset
	 * @param latitude - Current latitude
	 * @param zoom - Current zoom level
	 * @returns - Offset in degrees
	 */
	function pixelOffsetToLatitudinalDegrees(offsetPixels: number, latitude: number, zoom: number) {
		const earthCircumference = 40075016.686; // meters
		const latitudeRadians = (latitude * Math.PI) / 180;
		const pixelsPerMeter = (256 * Math.pow(2, zoom)) / earthCircumference / Math.cos(latitudeRadians);
		const metersPerPixel = 1 / pixelsPerMeter;
		const offsetMeters = offsetPixels * metersPerPixel;

		// Approximate conversion: 1 degree of latitude is ~111km
		const offsetDegrees = offsetMeters / 111000;

		return offsetDegrees;
	}

	/**
	 * Select a vessel on the map and fly to its position.
	 * This function calculates the appropriate latitude offset to ensure the vessel is visible on the map,
	 * and updates the point data and summary state accordingly. It also optionally fetches the last activities of the vessel.
	 * @param vessel - The vessel feature to select
	 * @param shouldGetLastActivities - Whether to fetch the last activities of the vessel
	 */
	const selectVesselOnMapAndFlyToPosition = useCallback(
		async (vessel: TFeature, shouldGetLastActivities?: boolean) => {
			const mapHeight = mapRef.current!.getCanvas().clientHeight;
			const mapZoomLevel = mapRef.current!.getZoom();
			const offsetPixels = mapHeight * 0.15;
			const originalLatitude = vessel.properties!.latitude;
			const originalLongitude = vessel.properties!.longitude;

			const newZoomLevel = mapZoomLevel <= DEFAULT_ZOOM_LEVEL ? 6 : mapZoomLevel;

			const latitudinalOffset = pixelOffsetToLatitudinalDegrees(offsetPixels, originalLatitude, newZoomLevel);
			const newLatitude = originalLatitude + latitudinalOffset;

			!shouldGetLastActivities && flyToPosition(originalLongitude, newLatitude, newZoomLevel);

			setPointSummary(null);
			setPointData({
				type: 'point',
				features: [vessel],
			});

			shouldGetLastActivities && getLastActivities(vessel.properties!.imoNumber);
		},
		[getLastActivities, flyToPosition, mapRef],
	);

	/**
	 * Effect to update vessels last signal when vesselsLastSignalData or selectedVesselsList changes.
	 * This effect filters the vesselsLastSignalData based on the selected vessels and updates the local state.
	 * If the output type is set to Vessel, it also selects the first vessel on the map and flies to its position.
	 * The effect ensures that it only runs when there are valid selected vessels and signal data available.
	 */
	useEffect(() => {
		if (selectionType === ESelectionType.Port) {
			return;
		}

		cleanUp();

		if (
			selectedVesselsList &&
			selectedVesselsList.length > 0 &&
			vesselsLastSignalData &&
			vesselsLastSignalData.features &&
			vesselsLastSignalData.features.length > 0
		) {
			const imoSet = new Set(selectedVesselsList.map((vessel) => vessel.imoNumber));

			const subscribedMsiVesselsSignals = vesselsLastSignalData?.features.filter((vessel) =>
				imoSet.has(vessel.properties!.imoNumber),
			);
			if (subscribedMsiVesselsSignals && subscribedMsiVesselsSignals.length > 0) {
				if (outputType === EOutputType.Vessel) {
					const selectedVessel = subscribedMsiVesselsSignals[0];
					selectVesselOnMapAndFlyToPosition(selectedVessel, true);
				}

				const signalInfo: TVesselInfo = {
					type: 'FeatureCollection',
					features: subscribedMsiVesselsSignals,
				};
				setVesselsLastSignal(signalInfo);
			} else {
				showWarningAlert('No signals found for the selected vessel(s)');
			}
		}
	}, [
		vesselsLastSignalData,
		selectVesselOnMapAndFlyToPosition,
		selectedVesselsList,
		outputType,
		selectionType,
		flyToPosition,
		cleanUp,
	]);

	useEffect(() => {
		if (selectionType !== ESelectionType.Port) {
			return;
		}
		cleanUp();
		const imoSet = new Set(selectedVesselsList.map((vessel) => vessel.imoNumber));
		const subscribedMsiVesselsSignals = vesselsLastSignalData?.features.filter((vessel) =>
			imoSet.has(vessel.properties!.imoNumber),
		);

		if (selectedPortGeometry == null) {
			flyToPosition(defaultViewState.longitude, defaultViewState.latitude, defaultViewState.zoom);
			showWarningAlert('No port geometry found for the selected port');
			return;
		}

		if (subscribedMsiVesselsSignals && subscribedMsiVesselsSignals.length > 0) {
			const signalInfo: TVesselInfo = {
				type: 'FeatureCollection',
				features: subscribedMsiVesselsSignals,
			};
			const centerPoint = findCenterPoint(signalInfo);
			flyToPosition(centerPoint.longitude, centerPoint.latitude, 10);
			setVesselsLastSignal(signalInfo);
		} else {
			// Create a proper GeoJSON feature for turf
			const portFeature: any = {
				type: 'Feature',
				geometry: selectedPortGeometry.geometry,
				properties: {},
			};

			// Get the centroid of the polygon/multipolygon
			const centroid = turf.centroid(portFeature);
			const [longitude, latitude] = centroid.geometry.coordinates;

			try {
				flyToPosition(longitude, latitude, 10); // Zoom level 10 for port view
			} catch (error) {
				console.error('Error calculating port geometry center:', error);
				// Fallback to default position if center calculation fails
				flyToPosition(defaultViewState.longitude, defaultViewState.latitude, defaultViewState.zoom);
			}
			showWarningAlert('No signals found for the selected port', { autoDismissTime: 2000 });
		}
	}, [selectionType, selectedVesselsList, selectedPortGeometry, flyToPosition, selectionType, outputType, cleanUp]);

	const onZoomEnd = useCallback(
		(event: ViewStateChangeEvent) => {
			try {
				const newZoomLevel = event.viewState.zoom;

				// Track zoom change
				trackEventWithContext(AppInsightsActions.MAP_ZOOM_CHANGE, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_ZOOM_CHANGE,
					additionalData: {
						previousZoom: zoomLevel,
						newZoom: newZoomLevel,
						zoomDelta: Math.abs(newZoomLevel - zoomLevel),
					},
				});

				setZoomLevel(newZoomLevel);
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_ZOOM_CHANGE,
					additionalData: {
						errorType: AppInsightsErrorTypes.MAP_ZOOM_ERROR,
						currentZoom: zoomLevel,
					},
				});
			}
		},
		[zoomLevel],
	);

	/**
	 * Handle click events on the map to select vessels and display their details.
	 * This function checks if a vessel point was clicked and updates the point data accordingly.
	 * If no features are clicked, it clears the point data unless there are ongoing voyages being displayed.
	 */
	const onClick = useCallback(
		(event: MapMouseEvent) => {
			if (event.features && event.features.length > 0) {
				const feature = event.features[0];
				if (feature.source?.startsWith('point-')) {
					if (feature.properties?.cluster) {
						// Cluster clicked — zoom in to expand it
						const map = mapRef.current?.getMap();
						const clusterId = feature.properties.cluster_id;
						const coords = (feature.geometry as unknown as { coordinates: [number, number] }).coordinates;
						const source = map?.getSource(feature.source) as any;
						source?.getClusterExpansionZoom(clusterId, (err: Error | null, zoom: number) => {
							if (err || !map) return;
							map.easeTo({ center: coords, zoom });
						});
					} else {
						const vesselFeatures = feature as unknown as TFeature;
						if (vesselFeatures) selectVesselOnMapAndFlyToPosition(vesselFeatures);
					}
				}
			} else {
				if (vesselLastVoyagesData === null) {
					setPointData(null);
				}
			}
		},
		[selectVesselOnMapAndFlyToPosition, vesselLastVoyagesData, setPointData, mapRef],
	);

	/**
	 * Handle mouse move events on the map to update cursor style and display point or voyage summaries.
	 * This function checks the features under the mouse pointer and updates the state accordingly.
	 * It sets different cursor styles based on the type of feature being hovered over,
	 * and populates the pointSummary or voyageSummary state with relevant information.
	 * If no interactive features are detected, it resets the cursor style and clears summaries.
	 */
	const onMouseMove = useCallback(
		(event: MapMouseEvent) => {
			const feature = event.features && event.features[0];
			if (feature && feature.properties) {
				setCursorStyle('default');
				if (feature.source === 'polygon') {
					setPointSummary({
						pointType: EPointType.POLYGON,
						type: feature.source,
						id: feature.properties.id,
						name: feature.properties.name,
						portName: feature.properties.portName,
						region: feature.properties.region,
						country: feature.properties.country,
						polygonType: feature.properties.polygonType,
						longitude: event.lngLat.lng,
						latitude: event.lngLat.lat,
					});
				} else if (feature.source?.startsWith('point-') || feature.source === 'vessel-arrows') {
					setCursorStyle('pointer');
					if (feature.properties.cluster) {
						setPointSummary(null);
					} else {
						setPointSummary({
							pointType: EPointType.VESSEL_LAST_POINT,
							type: feature.source,
							name: `${feature.properties.imoNumber} - ${feature.properties.vesselName}`,
							timestamp: feature.properties.timestamp,
							longitude: feature.properties.longitude,
							latitude: feature.properties.latitude,
							reportedDestination: feature.properties.reportedDestination,
							reportedEta: feature.properties.reportedEta,
						});
					}
				} else if (feature.source === 'vesselDailyPositions') {
					setCursorStyle('pointer');
					setPointSummary({
						pointType: EPointType.VESSEL_DAILY_POINT,
						type: feature.source,
						timestamp: feature.properties.timestamp,
						longitude: feature.properties.longitude,
						latitude: feature.properties.latitude,
						reportedDestination: feature.properties.reportedDestination,
						reportedEta: feature.properties.reportedEta,
					});
				} else if (feature.source === 'lines') {
					if (feature.properties.voyageId) {
						setFocusedVoyageId(feature.properties.voyageId);
						setVoyageSummary({
							originName: feature.properties.originName,
							originEndTime: feature.properties.originEndTime,
							destinationName: feature.properties.destinationName,
							destinationStartTime: feature.properties.destinationStartTime,
							longitude: event.lngLat.lng,
							latitude: event.lngLat.lat,
						});
					}
				} else if (feature.source === 'stoppage') {
					setPointSummary({
						pointType: EPointType.STOPPAGE_EVENT,
						type: feature.source,
						name: feature.properties.vesselActivity,
						startTime: feature.properties.startTime,
						endTime: feature.properties.endTime,
						totalTimeInPort: feature.properties.totalTimeInPort,
						longitude: event.lngLat.lng,
						latitude: event.lngLat.lat,
					});
				} else if (feature.source === 'port-calls') {
					setPointSummary({
						pointType: EPointType.PORT_CALL,
						type: feature.source,
						portName: feature.properties.portName,
						portActivity: feature.properties.portActivity || 'Unclassified',
						startTime: feature.properties.startTime,
						endTime: feature.properties.endTime,
						totalTimeInPort: feature.properties.totalTimeInPort,
						longitude: event.lngLat.lng,
						latitude: event.lngLat.lat,
					});
				}
			} else {
				setCursorStyle(''); // Reset cursor when not hovering over an interactive layer
				setFocusedVoyageId(0);
			}
		},
		[setCursorStyle, setPointSummary, setFocusedVoyageId, setVoyageSummary],
	);

	/**
	 * Reset cursor style and clear point and voyage summaries when the mouse leaves the map area.
	 */
	const onMouseLeave = useCallback(() => {
		setCursorStyle('');
		setPointSummary(null);
		setVoyageSummary(null);
	}, [setCursorStyle, setPointSummary, setVoyageSummary]);

	/**
	 * Handle vessel detail button click to show vessel details.
	 * This function extracts the IMO number from the provided vessel data
	 * and calls the handleVesselDetail function to display the vessel's details.
	 * It ensures that the vessel data is valid and contains features before proceeding.
	 * The function is memoized to prevent unnecessary re-renders and improve performance.
	 * @param vesselData - The vessel data containing features and properties
	 */
	const handleVesselDetailButtonOnClick = useCallback(
		async (vesselData: TVesselInfo) => {
			if (vesselData && vesselData.features && vesselData.features.length > 0) {
				handleVesselDetail(vesselData.features[0].properties!.imoNumber);
			}
		},
		[handleVesselDetail],
	);

	/**
	 * When vesselLastVoyagesData or vesselsLastSignalData changes, update the relevant states
	 * and fly to the position of the selected vessel.
	 * This effect runs only when outputType is 'Vessel' and a vessel is selected.
	 * It ensures that the map view is centered on the selected vessel's last known position.
	 * It also merges ongoing voyage signals if available.
	 * The effect depends on various data slices and settings to ensure it reacts to relevant changes.
	 * It also sets various vessel-related states for rendering on the map.
	 * The effect is optimized to avoid unnecessary executions by checking for data availability and conditions.
	 * It uses the flyToPosition function to smoothly transition the map view.
	 * It also handles antimeridian crossing for accurate rendering.
	 */
	useEffect(() => {
		if (
			outputType === EOutputType.Vessel &&
			vesselsLastSignalData &&
			vesselsLastSignalData.features &&
			vesselsLastSignalData.features.length > 0 &&
			selectedVesselsList &&
			selectedVesselsList.length > 0 &&
			vesselLastVoyagesData &&
			vesselLastVoyagesData.features &&
			vesselLastVoyagesData.features.length > 0
		) {
			// Filter and create vesselVoyagesTemp with selected vessel
			const vesselVoyagesTemp: TVesselInfo = {
				type: 'FeatureCollection',
				features: vesselLastVoyagesData.features.filter(
					(vessel) => vessel.properties!.imoNumber === selectedVesselsList[0].imoNumber,
				),
			};

			const signalFeatureInfo = vesselsLastSignalData?.features.find(
				(vessel) => vessel.properties!.imoNumber === selectedVesselsList[0].imoNumber,
			);

			if (!signalFeatureInfo) {
				cleanUp();
				return;
			}

			const signalInfo: TVesselInfo = {
				type: 'FeatureCollection',
				features: [signalFeatureInfo!],
			};

			const centerOfTheStoppageEvents = findCenterPoint(signalInfo!);

			// Fly to the calculated center
			flyToPosition(centerOfTheStoppageEvents.longitude, centerOfTheStoppageEvents.latitude, zoomLevel);

			// Merge vesselVoyages and ongoing signals
			if (vesselOngoingVoyageSignals && vesselOngoingVoyageSignals.vesselOngoingVoyageSignalsData) {
				vesselVoyagesTemp.features.push(...vesselOngoingVoyageSignals.vesselOngoingVoyageSignalsData.features);
			}

			const fixedVesselLastVoyagesData = fixAntimeridian(vesselVoyagesTemp);

			// Set various vessel data
			setVesselsLastSignal(signalInfo);
			setVesselVoyages(fixedVesselLastVoyagesData);
			setVesselVoyageDailySignals(vesselVoyageDailySignalsData);
			setVesselOngoingVoyageDailySignals(vesselOngoingVoyageDailySignalsData);

			// Set other related data
			if (vesselLastStoppageEventsData && vesselLastPortCallsData) {
				setVesselStoppageEvents(vesselLastStoppageEventsData);
				setVesselPortCalls(vesselLastPortCallsData);
			}

			if (vesselDailyPositionsAsLineString?.features && vesselDailyPositionsWithoutLastSignal?.features) {
				setVesselDailyPositionsAsLine(fixAntimeridian(vesselDailyPositionsAsLineString));
				setVesselDailyPositions(vesselDailyPositionsWithoutLastSignal);
			} else {
				setVesselDailyPositionsAsLine(null);
				setVesselDailyPositions(null);
			}
		}
	}, [
		vesselLastVoyagesData,
		vesselsLastSignalData,
		vesselLastStoppageEventsData,
		vesselLastPortCallsData,
		outputType,
		selectionType,
		selectedVesselsList,
		vesselOngoingVoyageSignals,
		vesselOngoingVoyageDailySignalsData,
		vesselVoyageDailySignalsData,
		vesselDailyPositionsWithoutLastSignal,
		vesselDailyPositionsAsLineString,
		flyToPosition,
	]);

	/**
	 * Handle map style changes when the user toggles between light and dark modes.
	 */
	const onMapStyleChange = useCallback(
		(event: any) => {
			try {
				let newStyle = mapStyle;
				let newStyleKey = mapStyleKey;
				const isLightMode = event.target.checked;

				// Track map style change attempt
				trackEventWithContext(AppInsightsActions.MAP_STYLE_CHANGE, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_STYLE_CHANGE,
					additionalData: {
						previousStyle: mapStyle,
						newMode: isLightMode ? 'Light' : 'Dark',
						styleKey: mapStyleKey,
					},
				});

				if (isLightMode) {
					const payload = JSON.stringify({ Mode: 'Light Mode' });
					const eventId = SeascapeActivityLogConstants.MAP_MODE_CHANGED;
					addSeascapeLogActivity({ eventId, payload });

					if (mapStyle === MapBoxGlobeMapStyles.Msi_Dusk) {
						newStyleKey = urlToStyleKey(MapBoxGlobeMapStyles.Msi_Day)!;
					} else if (mapStyle === MapBoxGlobeMapStyles.Msi_Dusk_Satellite) {
						newStyleKey = urlToStyleKey(MapBoxGlobeMapStyles.Msi_Day_Satellite)!;
					}
				} else {
					const payload = JSON.stringify({ Mode: 'Dark Mode' });
					const eventId = SeascapeActivityLogConstants.MAP_MODE_CHANGED;
					addSeascapeLogActivity({ eventId, payload });

					if (mapStyle === MapBoxGlobeMapStyles.Msi_Day) {
						newStyleKey = urlToStyleKey(MapBoxGlobeMapStyles.Msi_Dusk)!;
					} else if (mapStyle === MapBoxGlobeMapStyles.Msi_Day_Satellite) {
						newStyleKey = urlToStyleKey(MapBoxGlobeMapStyles.Msi_Dusk_Satellite)!;
					}
				}

				newStyle = styleKeyToUrl(newStyleKey);
				setMapStyle(newStyle);
				setMapStyleKey(newStyleKey);

				// Only persist dark/night (not satellite)
				if (
					newStyleKey === urlToStyleKey(MapBoxGlobeMapStyles.Msi_Dusk) ||
					newStyleKey === urlToStyleKey(MapBoxGlobeMapStyles.Msi_Dusk_Satellite)
				) {
					localStorage.setItem('userMapStyle', urlToStyleKey(MapBoxGlobeMapStyles.Msi_Dusk)!);
				} else if (
					newStyleKey === urlToStyleKey(MapBoxGlobeMapStyles.Msi_Day) ||
					newStyleKey === urlToStyleKey(MapBoxGlobeMapStyles.Msi_Day_Satellite)
				) {
					localStorage.setItem('userMapStyle', urlToStyleKey(MapBoxGlobeMapStyles.Msi_Day)!);
				}
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_STYLE_CHANGE,
					additionalData: {
						errorType: AppInsightsErrorTypes.MAP_STYLE_CHANGE_ERROR,
						currentStyle: mapStyle,
						currentStyleKey: mapStyleKey,
					},
				});
			}
		},
		[mapStyle, mapStyleKey, styleKeyToUrl, urlToStyleKey, addSeascapeLogActivity, setMapStyle, setMapStyleKey],
	);

	/**
	 * Handle map layer changes when the user selects a different map style.
	 */
	const onMapLayerChange = useCallback(
		(style: string) => {
			try {
				const layerType =
					style === MapBoxGlobeMapStyles.Msi_Globe
						? 'Globe'
						: style === MapBoxGlobeMapStyles.Msi_Day_Satellite
							? 'Light Satellite'
							: 'Dark Satellite';

				// Track map layer change
				trackEventWithContext(AppInsightsActions.MAP_LAYER_CHANGE, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_LAYER_CHANGE,
					additionalData: {
						previousStyle: mapStyle,
						newStyle: style,
						layerType,
					},
				});

				setMapStyle(style);

				const payload = JSON.stringify({ Style: layerType });
				const eventId = SeascapeActivityLogConstants.MAP_LAYER_CHANGED;
				addSeascapeLogActivity({ eventId, payload });
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.MAP_BOX_COMPONENT,
					action: AppInsightsActions.MAP_LAYER_CHANGE,
					additionalData: {
						errorType: AppInsightsErrorTypes.MAP_LAYER_ERROR,
						targetStyle: style,
						currentStyle: mapStyle,
					},
				});
			}
		},
		[addSeascapeLogActivity, setMapStyle],
	);

	/**
	 * Handle polygon layer toggle to show or hide detailed polygons on the map.
	 * This function checks if the detailed polygons are already loaded; if not, it dispatches an action to fetch them.
	 * It updates the state to show or hide the polygons based on the toggle state.
	 * The function is memoized to prevent unnecessary re-renders and improve performance.
	 * @param event - The change event from the toggle input
	 */
	const onPolygonLayerToggle = useCallback(
		(event: any) => {
			if (event.target.checked) {
				if (!detailedPolygons || !detailedPolygons.features || detailedPolygons.features.length === 0) {
					// If polygons are not loaded, dispatch the action to fetch them
					dispatch(detailedPolygonsActions.getDetailedPolygonsRequestAction());
				}
				setDetailedPolygonsVisible(true);
			} else {
				setDetailedPolygonsVisible(false);
			}
		},
		[dispatch, detailedPolygons, setDetailedPolygonsVisible],
	);

	const onOpenSeaMapToggle = useCallback(() => {
		setOpenSeaMapVisible(!openSeaMapVisible);

		// Optional: Log activity
		const payload = JSON.stringify({ Visible: !openSeaMapVisible });
		const eventId = SeascapeActivityLogConstants.MAP_LAYER_CHANGED;
		addSeascapeLogActivity({ eventId, payload });
	}, [openSeaMapVisible, setOpenSeaMapVisible, addSeascapeLogActivity]);

	/**
	 * Add colored arrow icons to the map for each unique color.
	 * @param map - mapboxgl.Map instance
	 * @param colors - Array of color strings
	 */
	const addColoredArrows = (map: mapboxgl.Map, colors: string[]) => {
		colors.forEach((color) => {
			if (!map.hasImage(`arrow-${color}`)) {
				const canvas = createArrowIcon(color);
				map.addImage(`arrow-${color}`, canvas, { pixelRatio: 2 });
			}
		});
	};

	/**
	 * Add arrow icons to the map for vessel direction points.
	 * This function checks if the map is available and if there are vessel signals to process.
	 * It extracts unique colors from the vessel signals and adds colored arrow icons to the map for each color.
	 * It also adds standard arrow icons for voyage direction points if they are not already present on the map.
	 * The function is memoized to prevent unnecessary re-renders and improve performance.
	 * It depends on the map reference, vessel signal data, and current map style to ensure accurate icon rendering.
	 */
	const addArrows = useCallback(() => {
		if (mapRef.current) {
			const map = mapRef.current.getMap();
			if (
				mapLoaded &&
				styleLoaded &&
				map &&
				vesselsLastSignalData &&
				vesselsLastSignalData.features &&
				vesselsLastSignalData.features.length > 0
			) {
				const uniqueColors = [...new Set(vesselsLastSignalData!.features.map((f) => f.properties!.color))];
				addColoredArrows(map, uniqueColors);
				setLayerImagesAdded(true);
			}

			// add arrow icon for voyage direction points
			if (!map.hasImage('arrow-icon')) {
				const canvas = createArrowIcon(mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#00355f' : '#ffffff');
				map.addImage('arrow-icon', canvas, { sdf: false });
				setLayerImagesAdded(true);
			}

			if (!map.hasImage('arrow-icon-long')) {
				const canvas = createArrowIcon(mapStyle === MapBoxGlobeMapStyles.Msi_Day ? '#00355f' : '#ffffff', 64, 128);
				map.addImage('arrow-icon-long', canvas, { sdf: false });
				setLayerImagesAdded(true);
			}
		}
	}, [mapRef, vesselsLastSignalData, mapStyle, mapLoaded, styleLoaded, setLayerImagesAdded]);

	/**
	 * Effect to handle map style loading events.
	 * This effect sets up event listeners for style loading events on the map instance.
	 * It updates the styleLoaded and layerImagesAdded states based on the loading status of the map style.
	 * The effect ensures that arrows are added to the map when the style is fully loaded.
	 * It cleans up the event listeners when the component unmounts or when dependencies change.
	 */
	useEffect(() => {
		const map = mapRef.current?.getMap();

		const handleStyleDataLoading = () => {
			// Style is starting to load - set to false
			setStyleLoaded(false);
			setLayerImagesAdded(false); // Reset this too since images will be lost
		};

		const handleStyleData = () => {
			// Style has finished loading
			if (map && map.isStyleLoaded()) {
				setStyleLoaded(true);
				addArrows(); // Add arrows when style is fully loaded
			}
		};

		const handleLoad = () => {
			setMapLoaded(true);
			if (map && map.isStyleLoaded()) {
				setStyleLoaded(true);
			}
		};

		if (map) {
			map.on('load', handleLoad);
			map.on('styledataloading', handleStyleDataLoading); // ADD THIS LINE
			map.on('styledata', handleStyleData);
		}

		return () => {
			if (map) {
				map.off('load', handleLoad);
				map.off('styledataloading', handleStyleDataLoading); // ADD THIS LINE
				map.off('styledata', handleStyleData);
			}
		};
	}, [addArrows]);

	/**
	 * Effect to add arrows to the map when the map is loaded and vessel signal data is available.
	 * This effect ensures that arrows are only added once the map is fully loaded and the necessary data is present.
	 * It updates the layerImagesAdded state to prevent redundant additions of arrow icons.
	 */
	useEffect(() => {
		if (
			mapLoaded &&
			styleLoaded &&
			vesselsLastSignalData &&
			vesselsLastSignalData.features &&
			vesselsLastSignalData.features.length > 0
		) {
			addArrows();
		}
	}, [vesselsLastSignalData, mapLoaded, styleLoaded, addArrows]);

	return (
		<>
			<Map
				ref={mapRef}
				{...settings}
				{...viewState}
				style={{ width: '100%', height: '100%' }}
				mapStyle={mapStyle}
				//mapStyle={MapBoxGlobeMapStyles.Msi_Dusk}
				attributionControl={false}
				onMove={(event) => setViewState(event.viewState)}
				onZoomEnd={onZoomEnd}
				onClick={onClick}
				onMouseMove={onMouseMove}
				onMouseLeave={onMouseLeave}
				interactiveLayerIds={[
					'stoppages',
					'polygons',
					'lines',
					'port-calls',
					'vessel-arrows-for-daily-positions',
					...Object.keys(vesselsByColor).map((key) => `vessel-arrows-${key}`),
				]} // Specify which layer is interactive
				cursor={cursorStyle}
				mapboxAccessToken={MapBoxToken}
				logoPosition="top-right"
				onLoad={() => {
					// Check if style is already loaded
					const map = mapRef.current?.getMap();
					if (!map) return;

					if (map.loaded()) {
						setMapLoaded(true);
					}

					if (map.isStyleLoaded()) {
						setStyleLoaded(true);
					}
				}}
				onStyleData={() => {
					// This fires when style loading completes OR when style changes
					const map = mapRef.current?.getMap();
					if (map && map.isStyleLoaded()) {
						setStyleLoaded(true);
					}
				}}
				onData={() => {
					// This fires when any data loads - use to check style load state
					const map = mapRef.current?.getMap();
					if (map && map.isStyleLoaded()) {
						setStyleLoaded(true);
					}
				}}
				projection={{
					name: 'mercator',
				}}
				maxTileCacheSize={0}
				customAttribution="© OpenSeaMap contributors"
			>
				<div>
					{layerImagesAdded && mapLoaded && styleLoaded && (
						<div>
							{vesselsLastSignal &&
								Object.entries(vesselsByColor).map(([colorKey, data]) => (
									<Source key={colorKey} id={`point-${colorKey}`} type="geojson" data={data}>
										<Layer
											{...pointArrowLayerStyle(
												selectionData.outputType === EOutputType.Vessel || selectionData.outputType === EOutputType.Fleet
													? 6
													: zoomLevel,
												colorKey,
											)}
											type="symbol"
										/>
									</Source>
								))}

							{openSeaMapVisible && (
								<div>
									<Source
										id="openseamap"
										type="raster"
										tiles={['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png']}
										tileSize={256}
									>
										<Layer
											id="openseamap-layer"
											type="raster"
											minzoom={0}
											maxzoom={22}
											paint={{
												'raster-opacity': 0.9, // Increase opacity to see markers more clearly
											}}
											beforeId={firstClusterLayerId}
										/>
									</Source>
								</div>
							)}

							{detailedPolygonsVisible && detailedPolygons && detailedPolygons.features.length > 0 && (
								<Source id="polygon" type="geojson" data={detailedPolygons}>
									<Layer {...polygonLayerStyle(mapStyle)} type="fill" beforeId={firstClusterLayerId} />
								</Source>
							)}

							{vesselStoppageEvents && (
								<Source id="stoppage" type="geojson" data={vesselStoppageEvents}>
									<Layer {...stoppagesLayerStyle(mapStyle)} type="circle" beforeId={firstClusterLayerId} />
								</Source>
							)}

							{vesselPortCalls && (
								<Source id="port-calls" type="geojson" data={vesselPortCalls}>
									<Layer {...portCallsLayerStyle()} type="circle" beforeId={vesselStoppageEvents ? 'stoppages' : undefined} />
								</Source>
							)}

							{vesselDailyPositions && (
								<Source id="vesselDailyPositions" type="geojson" data={vesselDailyPositions}>
									<Layer
										{...pointArrowLayerStyleForDailyPostitons()}
										type="symbol"
										beforeId={vesselPortCalls ? 'port-calls' : undefined}
									/>
								</Source>
							)}

							{vesselDailyPositionsAsLine && (
								<Source id="vesselDailyPositionsAsLine" type="geojson" data={vesselDailyPositionsAsLine}>
									<Layer
										{...lineLayerStyleForDailyPositions(mapStyle)}
										type="line"
										beforeId={vesselDailyPositions ? 'vessel-arrows-for-daily-positions' : undefined}
									/>
								</Source>
							)}

							{vesselVoyageDailySignals && (
								<Source id="voyageDailySignals" type="geojson" data={vesselVoyageDailySignals}>
									<Layer
										{...pointArrowLayerStyleForVoyage(focusedVoyageId)}
										type="symbol"
										beforeId={vesselPortCalls ? 'port-calls' : undefined}
									/>
								</Source>
							)}

							{vesselVoyages && (
								<Source id="lines" type="geojson" data={vesselVoyages}>
									<Layer
										{...lineLayerStyle(mapStyle, focusedVoyageId)}
										type="line"
										beforeId={vesselVoyageDailySignals ? 'vessel-arrows-for-voyage' : undefined}
									/>
								</Source>
							)}

							{vesselOngoingVoyageDailySignals && (
								<Source id="vesselOngoingVoyageDailySignals" type="geojson" data={vesselOngoingVoyageDailySignals}>
									<Layer
										{...pointArrowLayerStyleForOngoingVoyage(focusedVoyageId)}
										type="symbol"
										beforeId={vesselVoyages ? 'lines' : undefined}
									/>
								</Source>
							)}
						</div>
					)}

					{pointSummary && (
						<Popup
							longitude={pointSummary.longitude}
							latitude={pointSummary.latitude}
							closeButton={false}
							closeOnClick={false}
							onClose={() => setPointSummary(null)}
							anchor="bottom"
							focusAfterOpen
							maxWidth="700px"
						>
							<SignalHoverPopupContent info={pointSummary} />
						</Popup>
					)}

					{voyageSummary && (
						<Popup
							longitude={voyageSummary.longitude}
							latitude={voyageSummary.latitude}
							closeButton={false}
							closeOnClick={false}
							onClose={() => setVoyageSummary(null)}
							anchor="bottom"
							focusAfterOpen
							maxWidth="700px"
						>
							<VoyageHoverPopupContent info={voyageSummary} />
						</Popup>
					)}

					{pointData && !vesselLastVoyagesData && (
						<Popup
							className="rounded"
							longitude={pointData.features[0].properties!.longitude}
							latitude={pointData.features[0].properties!.latitude}
							closeButton={false}
							closeOnClick={true}
							anchor="bottom-left"
							focusAfterOpen
							key={pointData.features[0].properties!.imoNumber}
							maxWidth="500px"
						>
							<SignalPopupContent
								key={pointData.features[0].properties!.imoNumber}
								info={pointData}
								handleVesselDetailButtonOnClick={() => handleVesselDetailButtonOnClick(pointData)}
								isLoading={vesselLastVoyagesLoading || vesselLastStoppageEventsLoading || vesselLastPortCallsLoading}
								showVesselDetailButton={
									!(vesselsLastSignalData && vesselLastVoyagesData && vesselLastStoppageEventsData && vesselLastPortCallsData)
								}
							/>
						</Popup>
					)}

					{selectionData.outputType === EOutputType.Vessel && (
						<div className="absolute z-20" style={{ top: 11, left: 96 }}>
							<VesselPopupSummary
								key={'vessel-summary-' + selectionData.selectedVesselsList[0].imoNumber}
								info={selectionData.selectedVesselsList[0]}
							/>
						</div>
					)}

					{/* <div
						className="absolute z-0 flex flex-col bg-msi-black-color/0 rounded"
						style={{ bottom: 0, right: 0, minWidth: 60, width: 179, height: 100 }}
					></div> */}

					<MapLayerChanger changeMapStyle={onMapLayerChange} mapStyle={mapStyle} isEnabled={mapLoaded} />
					<MapLegendInfo />
					<MapStyleChanger changeMapStyle={onMapStyleChange} mapStyle={mapStyle} isEnabled={mapLoaded} />
					{isMsi && <PolygonLayerToggle onPolygonLayerToggle={onPolygonLayerToggle} />}
					{isMsi && <OpenSeaMapToggle isVisible={openSeaMapVisible} onToggle={onOpenSeaMapToggle} />}

					<NavigationControl position="bottom-right" showCompass={false} />

					<DateRangeWithSlider darkMode={mapStyle !== MapBoxGlobeMapStyles.Msi_Day} />
				</div>
			</Map>
		</>
	);
};

export default MapBox;
